import cx from 'classnames'
import {
  ChangeEvent,
  DragEvent,
  Reducer,
  useCallback,
  useContext,
  useId,
  useMemo,
  useReducer,
} from 'react'

import {
  SanityPrintSlotType,
  SanityPrintType,
} from '@data/sanity/queries/types/print'
import { SanityPrintColor } from '@data/sanity/queries/types/shop'
import { getRandomString } from '@lib/helpers'
import { LanguageContext } from '@lib/language'
import { uploadStateReducer } from '@lib/print/image'
import { PrintShopFormContext } from '@lib/print/print-shop-form-context'
import { PrintShopFormStringsContext } from '@lib/print/print-shop-form-strings-context'
import { PrintShopFormUploadContext } from '@lib/print/print-shop-form-upload-context'
import { UploadAction, UploadState } from '@lib/print/types'

interface UploadStepUploadAreaProps {
  printSlotType: SanityPrintSlotType
  printTypes: SanityPrintType[]
  printColor?: SanityPrintColor
}

const UploadStepUploadArea = ({
  printSlotType,
  printTypes,
  printColor,
}: UploadStepUploadAreaProps) => {
  const { locale } = useContext(LanguageContext)
  const { shopState } = useContext(PrintShopFormContext)
  const { shopFormStrings } = useContext(PrintShopFormStringsContext)
  const { uploadQueue, uploadErrors, addToUploadQueue } = useContext(
    PrintShopFormUploadContext,
  )

  const uploadAreaId = useMemo(() => {
    return [printTypes.join(','), printSlotType, printColor ?? 'no-color'].join(
      ',',
    )
  }, [printColor, printSlotType, printTypes])

  const isUploading = useMemo(() => {
    return uploadQueue.some((item) => item.uploadAreaId === uploadAreaId)
  }, [uploadAreaId, uploadQueue])

  const [uploadState, dispatchUploadState] = useReducer<
    Reducer<UploadState, UploadAction>
  >(uploadStateReducer, {
    dropDepth: 0,
    isInDropZone: false,
  })

  const handleDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    dispatchUploadState({
      type: 'updateDropDepth',
      dropDepthDelta: 1,
    })
  }, [])

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    dispatchUploadState({
      type: 'updateDropDepth',
      dropDepthDelta: -1,
    })
  }, [])

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    event.dataTransfer.dropEffect = 'copy'
    dispatchUploadState({
      type: 'setIsInDropZone',
      isInDropZone: true,
    })
  }, [])

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()

      const files = event.dataTransfer.files
        ? [...event.dataTransfer.files]
        : []

      if (files.length > 0) {
        // Add files to upload queue
        files.forEach((file) => {
          addToUploadQueue({
            id: getRandomString(),
            uploadAreaId,
            locale,
            shopId: shopState.id,
            file,
            printSlotType,
            printTypes,
            printColor,
            isOptimized: true,
          })
        })

        event.dataTransfer.clearData()
        dispatchUploadState({
          type: 'updateDropDepth',
          dropDepthDelta: -100,
        })
        dispatchUploadState({
          type: 'setIsInDropZone',
          isInDropZone: false,
        })
      }
    },
    [
      addToUploadQueue,
      locale,
      printColor,
      printSlotType,
      printTypes,
      shopState.id,
      uploadAreaId,
    ],
  )

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files ? [...event.target.files] : []

      if (files.length > 0) {
        // Add files to upload queue
        files.forEach((file) => {
          addToUploadQueue({
            id: getRandomString(),
            uploadAreaId,
            locale,
            shopId: shopState.id,
            file,
            printSlotType,
            printTypes,
            printColor,
            isOptimized: true,
          })
        })

        // Reset input value
        event.target.value = ''
      }
    },
    [
      addToUploadQueue,
      locale,
      printColor,
      printSlotType,
      printTypes,
      shopState.id,
      uploadAreaId,
    ],
  )

  const errorMessage = uploadErrors[uploadAreaId]
  const inputId = useId()

  return (
    <div
      role="presentation"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cx(
        'border border-dashed border-blue rounded-full transition-all',
        'hover:bg-blue hover:bg-opacity-5',
        {
          'bg-blue bg-opacity-5': uploadState.isInDropZone || isUploading,
        },
      )}
    >
      <label
        htmlFor={inputId}
        className="relative flex flex-col justify-center items-center p-3"
      >
        <input
          id={inputId}
          type="file"
          name="file"
          multiple
          onChange={handleChange}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />

        {isUploading && (
          <span className="leading-normal font-medium text-blue">
            {shopFormStrings.shopFormUploadingPrint}
          </span>
        )}
        {!isUploading && (
          <span
            className={cx('leading-normal font-medium', {
              'text-blue': !errorMessage,
              'text-red': errorMessage,
            })}
          >
            {errorMessage ? errorMessage : shopFormStrings.shopFormUploadPrint}
          </span>
        )}
      </label>
    </div>
  )
}

export default UploadStepUploadArea
